import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { COST_UNIT } from "../../Units";
import { normalizeDisplayLabel } from "../../utility/displayText";
import { EmptyState } from "../recharts/ReMixCharts/components/EmptyState";
import "../recharts/ReMixCharts/ReMixChart.css";
import { useEmptyStateVisibility } from "../recharts/ReMixCharts/hooks/useEmptyStateVisibility";
import { LegendSummaryTableProps, SortConfig } from "./types";
import { formatCellValue } from "./formatCellValue";

export const ChartTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
  block_key,
  filename,
  itemsPerPage: itemsPerPageProp,
  defaultSortKey: defaultSortKeyProp,
  defaultSortDirection,
  isSmall = false,
  serverSidePagination = false,
  totalCount = 0,
  currentServerPage = 1,
  onServerPageChange,
  onServerItemsPerPageChange,
  serverLoading = false,
  serverTotalPages,
  tableHeight,
  isLoading,
  emptyStateDelayMs = 450,
  editableColumn,
}) => {
  const dispatch = useDispatch();
  const defaultOrder = useMemo(
    () => [...extraKeys, ...legends.map((l) => l.dataKey)],
    [extraKeys, legends],
  );
  const defaultOrderStr = useMemo(() => defaultOrder.join(","), [defaultOrder]);

  const layoutStorageKeyBase = useMemo(() => {
    if (!filename) return "";
    return `ct_${filename}__${defaultOrder.join("__")}`;
  }, [filename, defaultOrder]);
  const orderStorageKey =
    layoutStorageKeyBase.length > 0 ? `${layoutStorageKeyBase}_order` : "";
  const widthStorageKey =
    layoutStorageKeyBase.length > 0 ? `${layoutStorageKeyBase}_widths` : "";

  const defaultSortKey =
    defaultSortKeyProp ||
    (extraKeys.length > 0 ? extraKeys[extraKeys.length - 1] : "");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    composite: false,
    key: defaultSortKey,
    direction: defaultSortDirection ?? "desc",
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageProp || 96,
  );
  const [jumpValue, setJumpValue] = useState<string>("");

  // ── Column drag-to-reorder + resize state ──
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultOrder);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [draggedColKey, setDraggedColKey] = useState<string | null>(null);
  const [dragOverColKey, setDragOverColKey] = useState<string | null>(null);
  const [recentlyMovedKey, setRecentlyMovedKey] = useState<string | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  // ── Inline edit state (single cell at a time) ──
  const [editingCellKey, setEditingCellKey] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string>("");
  const [draftError, setDraftError] = useState<string>("");

  const startCellEdit = (key: string, current: any) => {
    setEditingCellKey(key);
    setDraftValue(
      current === null || current === undefined || current === "-"
        ? ""
        : String(current),
    );
    setDraftError("");
  };

  const cancelCellEdit = () => {
    setEditingCellKey(null);
    setDraftValue("");
    setDraftError("");
  };

  const editableColumns = React.useMemo(
    () =>
      editableColumn == null
        ? []
        : Array.isArray(editableColumn)
          ? editableColumn
          : [editableColumn],
    [editableColumn],
  );
  const editableByKey = React.useMemo(() => {
    const m = new Map<string, (typeof editableColumns)[number]>();
    for (const c of editableColumns) m.set(c.dataKey, c);
    return m;
  }, [editableColumns]);

  const confirmCellEdit = (
    row: any,
    activeCol: (typeof editableColumns)[number] | undefined,
    options: { cancelOnInvalid?: boolean } = {},
  ) => {
    if (!activeCol) return cancelCellEdit();
    const trimmed = draftValue.trim();
    if (trimmed === "") return cancelCellEdit();
    const n = Number(trimmed);
    if (isNaN(n)) {
      if (options.cancelOnInvalid) return cancelCellEdit();
      setDraftError("Must be a number");
      return;
    }
    if (activeCol.min !== undefined && n < activeCol.min) {
      if (options.cancelOnInvalid) return cancelCellEdit();
      setDraftError(`Min ${activeCol.min}`);
      return;
    }
    if (activeCol.max !== undefined && n > activeCol.max) {
      if (options.cancelOnInvalid) return cancelCellEdit();
      setDraftError(`Max ${activeCol.max}`);
      return;
    }
    const prev = Number(row[activeCol.dataKey]);
    if (Number.isFinite(prev) && Math.abs(prev - n) < 1e-9) {
      return cancelCellEdit();
    }
    activeCol.onCellChange({
      rowKeyValue: row[activeCol.rowKey],
      newValue: n,
      row,
    });
    cancelCellEdit();
  };

  // Refs to measure each header's natural content width
  const headerContentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [headerMinWidths, setHeaderMinWidths] = useState<
    Record<string, number>
  >({});

  // Measured actual th offsetWidths for accurate sticky left positions
  const theadRowRef = useRef<HTMLTableRowElement | null>(null);
  const [measuredThWidths, setMeasuredThWidths] = useState<
    Record<string, number>
  >({});

  // Track what has been saved so we can show "unsaved changes" on the Save button
  const [savedSignature, setSavedSignature] = useState<string>("");

  const currentSignature = useMemo(
    () => JSON.stringify(columnOrder) + "|" + JSON.stringify(columnWidths),
    [columnOrder, columnWidths],
  );

  const hasChanges =
    Object.keys(columnWidths).length > 0 ||
    columnOrder.join(",") !== defaultOrderStr;

  // isDirty: only true when user HAS made changes AND those changes aren't saved yet
  // (prevents "Save layout" showing on fresh load with nothing saved)
  const isDirty =
    !!filename && hasChanges && currentSignature !== savedSignature;

  // Load persisted layout whenever table layout scope changes (filename/columns).
  useEffect(() => {
    setColumnOrder(defaultOrder);
    setColumnWidths({});

    if (!orderStorageKey || !widthStorageKey) {
      setSavedSignature("");
      return;
    }

    try {
      const savedOrderRaw = localStorage.getItem(orderStorageKey);
      const savedWidthsRaw = localStorage.getItem(widthStorageKey);

      const nextOrder = savedOrderRaw
        ? (() => {
            const parsed = JSON.parse(savedOrderRaw) as string[];
            const kept = parsed.filter((k) => defaultOrder.includes(k));
            const added = defaultOrder.filter((k) => !parsed.includes(k));
            return [...kept, ...added];
          })()
        : defaultOrder;

      const nextWidths = savedWidthsRaw
        ? (JSON.parse(savedWidthsRaw) as Record<string, number>)
        : {};

      setColumnOrder(nextOrder);
      setColumnWidths(nextWidths);
      setSavedSignature(
        JSON.stringify(nextOrder) + "|" + JSON.stringify(nextWidths),
      );
    } catch {
      setSavedSignature("");
    }
  }, [defaultOrder, orderStorageKey, widthStorageKey]);

  // Reset to page 1 whenever the incoming data changes (e.g. filter applied)
  useEffect(() => {
    if (!serverSidePagination) {
      setCurrentPage(1);
    }
  }, [data, serverSidePagination]);

  const effectivePage = serverSidePagination ? currentServerPage : currentPage;

  useEffect(() => {
    setSelectedRowIndex(null);
  }, [effectivePage, data]);

  useEffect(() => {
    setHoveredRowIndex(null);
  }, [effectivePage, data]);

  const handlePageSet = (page: number) => {
    if (serverSidePagination && onServerPageChange) {
      onServerPageChange(page);
    } else {
      setCurrentPage(page);
    }
  };

  const handleSort = (columnKey: string) => {
    if (sortConfig.composite || sortConfig.key !== columnKey) {
      setSortConfig({ composite: false, key: columnKey, direction: "desc" });
    } else {
      setSortConfig({
        composite: false,
        key: columnKey,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    }
    // Only reset page for client-side pagination; server-side sorts current page in place
    if (!serverSidePagination) {
      handlePageSet(1);
    }
  };

  const MONTH_ABBR = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parseMonthStr = (v: string): number | null => {
    const m = /^([A-Za-z]{3})-(\d{2})$/.exec(v);
    if (!m) return null;
    const mon = MONTH_ABBR.findIndex(
      (x) => x.toLowerCase() === m[1].toLowerCase(),
    );
    if (mon === -1) return null;
    return parseInt(m[2]) * 12 + mon;
  };

  const parseDDMMYYYY = (v: string): number | null => {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (!m) return null;
    return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime();
  };

  const compareValues = (aValue: any, bValue: any): number => {
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;
    if (typeof aValue === "number" && typeof bValue === "number") {
      return aValue - bValue;
    }
    const aStr = aValue.toString();
    const bStr = bValue.toString();
    const aMonth = parseMonthStr(aStr);
    const bMonth = parseMonthStr(bStr);
    if (aMonth !== null && bMonth !== null) {
      return aMonth - bMonth;
    }
    const aDate = parseDDMMYYYY(aStr);
    const bDate = parseDDMMYYYY(bStr);
    if (aDate !== null && bDate !== null) {
      return aDate - bDate;
    }
    return aStr.localeCompare(bStr);
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortConfig.composite) {
        let cmp = compareValues(a[extraKeys[0]], b[extraKeys[0]]);
        if (cmp === 0 && extraKeys.length > 1) {
          cmp = compareValues(a[extraKeys[1]], b[extraKeys[1]]);
        }
        return sortConfig.direction === "asc" ? cmp : -cmp;
      } else if (sortConfig.key) {
        let cmp = compareValues(a[sortConfig.key], b[sortConfig.key]);
        return sortConfig.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig, extraKeys]);

  const totalPages = serverSidePagination
    ? (serverTotalPages ?? Math.ceil(totalCount / itemsPerPage))
    : Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    if (serverSidePagination) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [serverSidePagination, sortedData, currentPage, itemsPerPage]);

  const resolvedLoading = isLoading ?? serverLoading;
  const showEmpty = useEmptyStateVisibility({
    isEmpty: paginatedData.length === 0,
    isLoading: resolvedLoading,
    delayMs: emptyStateDelayMs,
  });

  const renderSortArrow = (columnKey: string) => {
    if (!sortConfig.composite && sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  function calculateLegends(legend: any) {
    const displayName = normalizeDisplayLabel(legend.name).trim();
    if (/\(forecast\)\s*$/i.test(displayName)) return displayName;

    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const trailingParenMatch = displayName.match(/\(([^)]*)\)\s*$/);
    const trailingParenValue = trailingParenMatch?.[1]?.trim().toLowerCase();
    const suffixValue = String(suffix || "")
      .trim()
      .toLowerCase();
    const cleanName =
      trailingParenValue && suffixValue && trailingParenValue === suffixValue
        ? displayName.replace(/\s*\([^)]*\)\s*$/, "").trim()
        : displayName;
    return `${cleanName} (${suffix})`;
  }

  const headerColumns = [
    ...extraKeys.map((key, index) => ({
      key,
      label: normalizeDisplayLabel(extraLabels[index] || key),
    })),
    ...legends.map((legend) => ({
      key: legend.dataKey,
      // label: `${legend.name} (${unit?.toUpperCase() ?? legend.unit})`,
      label: calculateLegends(legend),
    })),
  ];

  // Sync columnOrder when legends/extraKeys props change
  const colKeyStr = headerColumns.map((c) => c.key).join(",");
  useEffect(() => {
    setColumnOrder((prev) => {
      const keys = headerColumns.map((c) => c.key);
      const kept = prev.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !prev.includes(k));
      if (kept.length === prev.length && added.length === 0) return prev;
      return [...kept, ...added];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colKeyStr]);

  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((k) => headerColumns.find((c) => c.key === k))
        .filter(Boolean) as typeof headerColumns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnOrder, colKeyStr],
  );

  const stickyColumnPositions = useMemo(() => {
    const stickyNames = new Set([
      "month",
      "year",
      "timeslot",
      "timeblock",
      "date",
      "dateindex",
      "slot",
      "block",
    ]);
    const normalize = (value: string) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const stickyCols = orderedColumns.filter((col) => {
      const k = normalize(col.key);
      const l = normalize(col.label);
      return stickyNames.has(k) || stickyNames.has(l);
    });

    const positionMap: Record<string, { left: number; showShadow: boolean }> =
      {};
    let leftOffset = 0;
    stickyCols.forEach((col, idx) => {
      const width =
        measuredThWidths[col.key] ||
        Math.max(
          columnWidths[col.key] || 0,
          headerMinWidths[col.key] || 0,
          100,
        );
      positionMap[col.key] = {
        left: leftOffset,
        showShadow: idx === stickyCols.length - 1,
      };
      leftOffset += width;
    });
    return positionMap;
  }, [orderedColumns, columnWidths, headerMinWidths, measuredThWidths]);

  const resetColumns = () => {
    setColumnOrder(headerColumns.map((c) => c.key));
    setColumnWidths({});
    if (orderStorageKey && widthStorageKey) {
      try {
        localStorage.removeItem(orderStorageKey);
        localStorage.removeItem(widthStorageKey);
      } catch {}
      setSavedSignature("");
    }
  };

  const saveColumns = () => {
    if (!orderStorageKey || !widthStorageKey) return;
    try {
      localStorage.setItem(orderStorageKey, JSON.stringify(columnOrder));
      localStorage.setItem(widthStorageKey, JSON.stringify(columnWidths));
      setSavedSignature(currentSignature);
    } catch {}
  };

  const handleDownloadCSV = () => {
    const legendsByKey = new Map(
      legends.map((legend) => [legend.dataKey, legend]),
    );
    const headers = orderedColumns.map((col) => {
      const legend = legendsByKey.get(col.key);
      if (legend) return calculateLegends(legend);
      const extraIdx = extraKeys.indexOf(col.key);
      return extraIdx !== -1
        ? normalizeDisplayLabel(extraLabels[extraIdx] || col.key)
        : col.label;
    });

    const formatNum = (value: any) => {
      if (value === undefined || value === null || value === "") return "";
      const num = Number(value);
      return isNaN(num) ? String(value) : num.toFixed(2);
    };

    const escapeCsv = (value: string | number) => {
      const str = String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows = sortedData.map((row) =>
      orderedColumns.map((col) => {
        const legend = legendsByKey.get(col.key);
        if (legend) {
          const raw = row[legend.dataKey];
          if (raw == null) return "";
          const formatted = formatNum(raw);
          if (
            legend.deviationValueKey &&
            row[legend.deviationValueKey] != null
          ) {
            return escapeCsv(
              `${formatted} (${formatNum(row[legend.deviationValueKey])})`,
            );
          }
          return escapeCsv(formatted);
        }

        return escapeCsv(row[col.key] ?? "");
      }),
    );

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", (filename || "table") + " Chart Data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Measure each header label width via canvas so it's independent of layout
  // (scrollWidth returns constrained width when flex children shrink in a fixed-width th)
  useLayoutEffect(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Try to read actual font from a rendered header; fall back to approximation
    let fontStr = "";
    const sampleKey = orderedColumns[0]?.key;
    const sampleEl = sampleKey ? headerContentRefs.current[sampleKey] : null;
    if (sampleEl) {
      const cs = window.getComputedStyle(sampleEl);
      fontStr = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    } else {
      fontStr = isSmall ? "500 10px sans-serif" : "500 14px sans-serif";
    }
    ctx.font = fontStr;
    // Fixed chrome: grip icon + gaps + sort arrow + th padding + resize handle + safety
    const chrome = isSmall ? 64 : 92;
    const measured: Record<string, number> = {};
    orderedColumns.forEach((col) => {
      const w = ctx.measureText(col.label).width;
      measured[col.key] = Math.ceil(w + chrome);
    });
    setHeaderMinWidths((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(measured)) {
        if ((prev[k] || 0) !== v) {
          next[k] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [orderedColumns, isSmall]);

  // Sync actual th offsetWidths so sticky left offsets are pixel-accurate
  useLayoutEffect(() => {
    const tr = theadRowRef.current;
    if (!tr) return;
    const ths = Array.from(tr.querySelectorAll("th")) as HTMLTableCellElement[];
    const next: Record<string, number> = {};
    ths.forEach((th, i) => {
      const key = orderedColumns[i]?.key;
      if (key) next[key] = th.offsetWidth;
    });
    setMeasuredThWidths((prev) => {
      const changed = orderedColumns.some(
        (c) => (prev[c.key] || 0) !== (next[c.key] || 0),
      );
      return changed ? next : prev;
    });
  }, [orderedColumns, columnWidths, headerMinWidths]);

  const renderCell = (
    col: { key: string; label: string },
    row: any,
    opts: {
      isSticky?: boolean;
      stickyLeft?: number;
      showStickyShadow?: boolean;
      isSelected?: boolean;
      isHovered?: boolean;
    } = {},
  ) => {
    const isFixed = Object.keys(columnWidths).length > 0;
    const fixedCellStyle: React.CSSProperties | undefined = isFixed
      ? {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "1px",
        }
      : undefined;
    const stickyStyle: React.CSSProperties = opts.isSticky
      ? {
          position: "sticky",
          left: opts.stickyLeft ?? 0,
          zIndex: 5,
          overflow: "visible",
          boxShadow: opts.showStickyShadow
            ? "8px 0 14px -12px rgba(15, 23, 42, 0.42), 14px 0 24px -20px rgba(15, 23, 42, 0.24)"
            : undefined,
        }
      : {};
    const firstColClass = opts.isSticky
      ? opts.isSelected
        ? "bg-orange-50"
        : opts.isHovered
          ? "bg-gray-100"
          : "bg-white"
      : "";
    const mergedCellStyle: React.CSSProperties | undefined =
      isFixed || opts.isSticky
        ? { ...(fixedCellStyle || {}), ...stickyStyle }
        : undefined;
    const extraIndex = extraKeys.indexOf(col.key);
    if (extraIndex !== -1) {
      const key = extraKeys[extraIndex];
      return (
        <td
          key={col.key}
          className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} text-center ${
            key === "block" ? "text-[#F1935C] font-medium" : ""
          } ${firstColClass}`}
          style={mergedCellStyle}
          title={
            isFixed && row[key] !== undefined ? String(row[key]) : undefined
          }
        >
          {row[key] !== undefined
            ? typeof row[key] === "string"
              ? normalizeDisplayLabel(row[key])
              : row[key]
            : "-"}
        </td>
      );
    }
    const legend = legends.find((l) => l.dataKey === col.key);
    if (legend) {
      const activeEditableCol = editableByKey.get(col.key);
      const isEditableCol = !!activeEditableCol;
      const rowKeyVal = isEditableCol
        ? row[activeEditableCol!.rowKey]
        : undefined;
      const cellKey = isEditableCol
        ? `${activeEditableCol!.dataKey}__${String(rowKeyVal)}`
        : "";
      const isEditing = isEditableCol && editingCellKey === cellKey;
      const currentNumeric = isEditableCol ? Number(row[col.key]) : NaN;
      const originalNumeric =
        isEditableCol && activeEditableCol!.originalByRowKey
          ? activeEditableCol!.originalByRowKey[rowKeyVal]
          : undefined;
      const differs =
        isEditableCol &&
        typeof originalNumeric === "number" &&
        Number.isFinite(currentNumeric) &&
        Math.abs(originalNumeric - currentNumeric) > 1e-9;
      const cellText =
        legend.deviationValueKey && row[legend.deviationValueKey] != null
          ? `${formatCellValue(row[legend.dataKey])} (${formatCellValue(row[legend.deviationValueKey])})`
          : formatCellValue(row[legend.dataKey]);
      return (
        <td
          key={col.key}
          className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} ${firstColClass} ${
            isEditableCol ? "relative cursor-pointer" : ""
          }`}
          style={
            isEditing
              ? { ...(mergedCellStyle || {}), overflow: "visible" }
              : mergedCellStyle
          }
          title={isFixed && !isEditing ? cellText : undefined}
          onClick={(e) => {
            if (!isEditableCol || isEditing) return;
            e.stopPropagation();
            startCellEdit(cellKey, row[activeEditableCol!.dataKey]);
          }}
        >
          {differs && !isEditing && activeEditableCol!.onRevert && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                activeEditableCol!.onRevert!(rowKeyVal);
              }}
              className="absolute top-0 right-0 bg-amber-500 hover:bg-amber-600 text-white rounded-bl text-[10px] px-1.5 py-[1px] leading-tight shadow z-[9]"
              title="Revert"
            >
              ↺
            </button>
          )}
          {!isEditing && (
            <span className={differs ? "text-[#F1935C] font-semibold" : ""}>
              {cellText}
            </span>
          )}
          {isEditing && (
            <div className="absolute z-[9999] inset-0 flex items-center justify-center">
              <div className="flex flex-col items-stretch">
                {draftError && (
                  <div className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mb-0.5 whitespace-nowrap">
                    {draftError}
                  </div>
                )}
                <div className="flex border border-gray-300 shadow-lg bg-white rounded">
                  <input
                    autoFocus
                    type="number"
                    inputMode="decimal"
                    step={activeEditableCol!.step ?? "any"}
                    min={activeEditableCol!.min}
                    max={activeEditableCol!.max}
                    className="w-20 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0 rounded-l"
                    value={draftValue}
                    onChange={(e) => {
                      setDraftValue(e.target.value);
                      setDraftError("");
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter")
                        confirmCellEdit(row, activeEditableCol);
                      else if (e.key === "Escape") cancelCellEdit();
                    }}
                    onBlur={(e) => {
                      if (
                        !e.currentTarget.parentElement?.contains(
                          e.relatedTarget as Node,
                        )
                      ) {
                        confirmCellEdit(row, activeEditableCol, {
                          cancelOnInvalid: true,
                        });
                      }
                    }}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmCellEdit(row, activeEditableCol);
                    }}
                    className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0 rounded-r"
                    title="Save"
                  >
                    ✓
                  </button>
                </div>
              </div>
            </div>
          )}
        </td>
      );
    }
    return (
      <td key={col.key} className={firstColClass} style={mergedCellStyle}>
        -
      </td>
    );
  };

  const handlePreviousPage = () => {
    if (effectivePage > 1) handlePageSet(effectivePage - 1);
  };

  const handleNextPage = () => {
    if (effectivePage < totalPages) handlePageSet(effectivePage + 1);
  };

  const handleJump = () => {
    const page = parseInt(jumpValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      handlePageSet(page);
    }
    setJumpValue("");
  };

  const handleItemsPerPageChange = (newSize: number) => {
    setItemsPerPage(newSize);
    if (serverSidePagination && onServerItemsPerPageChange) {
      onServerItemsPerPageChange(newSize);
    }
    handlePageSet(1);
  };

  // Always returns exactly 7 slots so the layout never shifts.
  const getPageButtons = (): (number | "..." | null)[] => {
    const SLOTS = 7;
    if (totalPages <= SLOTS) {
      return [
        ...Array.from({ length: totalPages }, (_, i) => i + 1),
        ...Array<null>(SLOTS - totalPages).fill(null),
      ] as (number | null)[];
    }
    if (effectivePage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (effectivePage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      effectivePage - 1,
      effectivePage,
      effectivePage + 1,
      "...",
      totalPages,
    ];
  };

  useEffect(() => {
    dispatch(setLoadingFilterName(""));
  }, [dispatch]);

  // Compute which side to show the drop indicator based on drag direction
  const draggedIdx = draggedColKey ? columnOrder.indexOf(draggedColKey) : -1;
  const dragOverIdx = dragOverColKey ? columnOrder.indexOf(dragOverColKey) : -1;
  const dropSide =
    draggedIdx !== -1 && dragOverIdx !== -1 && draggedColKey !== dragOverColKey
      ? draggedIdx > dragOverIdx
        ? "left"
        : "right"
      : null;

  return (
    <div
      className={`chartTable ${isSmall ? "isSmall" : ""} flex flex-col tab-animatable-content`}
      style={
        isSmall ? undefined : { height: tableHeight ?? "calc(100vh - 315px)" }
      }
    >
      <style>{`
        @keyframes ctColMoved {
          0% { background-color: #fdd3bb; box-shadow: inset 0 -2px 0 0 #f1935c; }
          60% { background-color: #fff3ed; box-shadow: inset 0 -2px 0 0 rgba(241,147,92,0.4); }
          100% { background-color: transparent; box-shadow: none; }
        }
      `}</style>
      {/* In small mode, avoid inner vertical scrolling to prevent double scrollbars */}
      <div
        className={`${isSmall ? "overflow-x-auto overflow-y-visible" : "flex-1 min-h-0 overflow-auto text-nowrap"} capitalize relative`}
      >
        <table
          className={`${isSmall ? "w-full" : ""}`}
          style={
            Object.keys(columnWidths).length > 0
              ? { tableLayout: "fixed" }
              : undefined
          }
        >
          {Object.keys(columnWidths).length > 0 && (
            <colgroup>
              {orderedColumns.map((col) => (
                <col
                  key={col.key}
                  style={{
                    width: columnWidths[col.key]
                      ? `${Math.max(columnWidths[col.key], headerMinWidths[col.key] || 0)}px`
                      : undefined,
                    minWidth: `${Math.max(100, headerMinWidths[col.key] || 100)}px`,
                  }}
                />
              ))}
            </colgroup>
          )}
          <thead className="sticky top-0 z-20">
            <tr
              ref={theadRowRef}
              className={isSmall ? "text-[10px]" : "text-sm"}
            >
              {orderedColumns.map((col, colIdx) => (
                <th
                  key={col.key}
                  className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} cursor-pointer text-center relative select-none`}
                  style={{
                    opacity: draggedColKey === col.key ? 0.35 : 1,
                    transition:
                      "opacity 0.2s ease, background 0.15s ease, border-left 0.1s ease, border-right 0.1s ease",
                    background:
                      dragOverColKey === col.key && draggedColKey !== col.key
                        ? "#fff3ed"
                        : "white",
                    borderLeft:
                      dragOverColKey === col.key &&
                      dropSide === "left" &&
                      draggedColKey !== col.key
                        ? "2px solid #f1935c"
                        : undefined,
                    borderRight:
                      dragOverColKey === col.key &&
                      dropSide === "right" &&
                      draggedColKey !== col.key
                        ? "2px solid #f1935c"
                        : undefined,
                    animation:
                      recentlyMovedKey === col.key
                        ? "ctColMoved 0.55s ease"
                        : undefined,
                    ...(stickyColumnPositions[col.key]
                      ? {
                          position: "sticky" as const,
                          left: stickyColumnPositions[col.key].left,
                          top: 0,
                          zIndex: 30,
                          boxShadow: stickyColumnPositions[col.key].showShadow
                            ? "8px 0 14px -12px rgba(15, 23, 42, 0.42), 14px 0 24px -20px rgba(15, 23, 42, 0.24)"
                            : undefined,
                        }
                      : {}),
                    ...(Object.keys(columnWidths).length > 0 && colIdx !== 0
                      ? { overflow: "hidden" }
                      : {}),
                  }}
                  draggable
                  onDragStart={(e) => {
                    setDraggedColKey(col.key);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDragOverColKey(col.key);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!draggedColKey || draggedColKey === col.key) {
                      setDraggedColKey(null);
                      setDragOverColKey(null);
                      return;
                    }
                    const fromKey = draggedColKey;
                    setColumnOrder((order) => {
                      const next = [...order];
                      const fromIdx = next.indexOf(fromKey);
                      const toIdx = next.indexOf(col.key);
                      if (fromIdx === -1 || toIdx === -1) return order;
                      next.splice(fromIdx, 1);
                      next.splice(toIdx, 0, fromKey);
                      return next;
                    });
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                    setRecentlyMovedKey(fromKey);
                    window.setTimeout(() => setRecentlyMovedKey(null), 600);
                  }}
                  onDragEnd={() => {
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <div
                    ref={(el) => {
                      headerContentRefs.current[col.key] = el;
                    }}
                    className="flex items-center justify-center gap-1 whitespace-nowrap"
                  >
                    <span
                      className="text-gray-400 cursor-grab active:cursor-grabbing shrink-0 hover:text-[#f1935c] transition-colors duration-150"
                      title="Drag to reorder"
                    >
                      ⠿
                    </span>
                    {(() => {
                      const match = col.label.match(
                        /(.*)\s*\((MW|MWH|MUS|Rs\/kWh|LU|₹\/kWh|Rs|%|°C|km\/h|mm|s|W\/m²)\)$/i,
                      );
                      if (match) {
                        const [, nameText, unitText] = match;
                        return (
                          <>
                            <span className="font-medium shrink-0">
                              {nameText}{" "}
                            </span>
                            <span className="text-[#34656D] ms-1 font-medium shrink-0">
                              ({unitText})
                            </span>
                          </>
                        );
                      } else {
                        return (
                          <span className="shrink-0">
                            {col.label.replace("()", "")}
                          </span>
                        );
                      }
                    })()}
                    <span className="ml-1">{renderSortArrow(col.key)}</span>
                  </div>
                  {/* Resize handle — drag right edge to resize column width */}
                  <div
                    className="absolute right-0 top-0 h-full w-5 cursor-col-resize z-10 flex items-center justify-center group/rz"
                    title="Drag to resize"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const th = e.currentTarget
                        .parentElement as HTMLTableCellElement;
                      const key = col.key;
                      const startX = e.clientX;
                      const startWidth = th?.offsetWidth ?? 120;

                      // On first resize: snapshot ALL column offsetWidths from the DOM so
                      // switching to table-layout:fixed doesn't collapse un-resized columns
                      if (Object.keys(columnWidths).length === 0) {
                        const tr = th.closest("tr");
                        if (tr) {
                          const allThs = Array.from(
                            tr.querySelectorAll("th"),
                          ) as HTMLTableCellElement[];
                          const snapshot: Record<string, number> = {};
                          allThs.forEach((thEl, i) => {
                            const colKey = orderedColumns[i]?.key;
                            if (colKey) snapshot[colKey] = thEl.offsetWidth;
                          });
                          setColumnWidths(snapshot);
                        }
                      }

                      document.body.style.userSelect = "none";
                      document.body.style.cursor = "col-resize";
                      const floor = Math.max(100, headerMinWidths[key] || 100);
                      const onMove = (me: MouseEvent) => {
                        const newWidth = Math.max(
                          floor,
                          startWidth + me.clientX - startX,
                        );
                        setColumnWidths((prev) => ({
                          ...prev,
                          [key]: newWidth,
                        }));
                      };
                      const onUp = () => {
                        document.body.style.userSelect = "";
                        document.body.style.cursor = "";
                        document.removeEventListener("mousemove", onMove);
                        document.removeEventListener("mouseup", onUp);
                      };
                      document.addEventListener("mousemove", onMove);
                      document.addEventListener("mouseup", onUp);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="h-[45%] w-[2px] rounded-full bg-gray-300/60 group-hover/rz:bg-[#f1935c] group-hover/rz:h-[80%] group-hover/rz:w-[3px] group-hover/rz:shadow-sm transition-all duration-150" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            className={`text-[#8C8A96] font-normal ${isSmall ? "text-[11px]" : "text-[13px]"}`}
          >
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                onMouseEnter={() => setHoveredRowIndex(rowIndex)}
                onMouseLeave={() => setHoveredRowIndex(null)}
                onClick={() =>
                  setSelectedRowIndex((prev) =>
                    prev === rowIndex ? null : rowIndex,
                  )
                }
                className={`border-b text-center cursor-pointer ${
                  rowIndex === selectedRowIndex
                    ? "bg-orange-50 border-orange-200"
                    : rowIndex === hoveredRowIndex
                      ? "bg-gray-100 border-gray-200"
                      : "border-gray-200"
                }`}
              >
                {orderedColumns.map((col, colIdx) =>
                  renderCell(col, row, {
                    isSticky: !!stickyColumnPositions[col.key],
                    stickyLeft: stickyColumnPositions[col.key]?.left,
                    showStickyShadow:
                      stickyColumnPositions[col.key]?.showShadow,
                    isSelected: rowIndex === selectedRowIndex,
                    isHovered: rowIndex === hoveredRowIndex,
                  }),
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {paginatedData.length === 0 && showEmpty && (
          <EmptyState compact={isSmall} />
        )}
      </div>

      {/* Loading overlay */}
      {resolvedLoading && (
        <div className="absolute inset-0 bg-white/60 z-30 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#f1935c] border-t-transparent rounded-full" />
        </div>
      )}

      {/* ── Pagination bar ── */}

      <div
        className={`flex flex-wrap items-center justify-between gap-2 shrink-0 bg-white border-t border-gray-200 z-20 px-2 ${
          isSmall ? "text-[10px] py-1" : "text-sm py-2"
        }`}
      >
        {/* Left: record-range info + column controls */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-gray-500 whitespace-nowrap shrink-0 text-xs">
            {(() => {
              const total = serverSidePagination
                ? totalCount
                : sortedData.length;
              if (total === 0) return "No records";
              return `${(effectivePage - 1) * itemsPerPage + 1}–${Math.min(
                effectivePage * itemsPerPage,
                total,
              )} of ${total}`;
            })()}
          </span>

          {/* Column layout controls — always visible */}
          {(() => {
            return (
              <div className="flex items-center gap-1.5">
                {filename && (
                  <button
                    onClick={saveColumns}
                    disabled={!isDirty}
                    title={
                      !hasChanges
                        ? "Drag ⠿ to reorder columns · Drag column edge to resize"
                        : isDirty
                          ? "Save current layout so it loads next time"
                          : "Layout already saved"
                    }
                    className={`inline-flex items-center gap-1 rounded-md whitespace-nowrap shrink-0 transition-all duration-150 font-medium ${
                      isSmall
                        ? "text-[9px] px-1.5 py-0.5"
                        : "text-[11px] px-2.5 py-1"
                    } ${
                      isDirty
                        ? "bg-[#f1935c] text-white shadow-sm hover:bg-[#e07840] active:scale-95 cursor-pointer"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                    }`}
                  >
                    {isDirty ? (
                      <>
                        <svg
                          className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                          <polyline points="17 21 17 13 7 13 7 21" />
                          <polyline points="7 3 7 8 15 8" />
                        </svg>
                        Save layout
                      </>
                    ) : (
                      <>
                        <svg
                          className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {hasChanges ? "Saved" : "Save layout"}
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={hasChanges ? resetColumns : undefined}
                  disabled={!hasChanges}
                  title={
                    hasChanges
                      ? "Reset column order and widths to default"
                      : "Drag ⠿ to reorder columns · Drag column edge to resize"
                  }
                  className={`inline-flex items-center gap-1 rounded-md border whitespace-nowrap shrink-0 transition-all duration-150 font-medium ${
                    isSmall
                      ? "text-[9px] px-1.5 py-0.5"
                      : "text-[11px] px-2.5 py-1"
                  } ${
                    hasChanges
                      ? "border-gray-200 text-gray-500 bg-white hover:border-gray-300 hover:bg-gray-50 active:scale-95 cursor-pointer"
                      : "border-gray-200 text-gray-300 bg-white cursor-not-allowed opacity-60"
                  }`}
                >
                  <svg
                    className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                  Reset layout
                </button>

                {/* <button
                  onClick={handleDownloadCSV}
                  disabled={sortedData.length === 0}
                  title={`Download all ${sortedData.length} rows as CSV (current column order)`}
                  className={`inline-flex items-center gap-1 rounded-md border whitespace-nowrap shrink-0 transition-all duration-150 font-medium ${
                    isSmall
                      ? "text-[9px] px-1.5 py-0.5"
                      : "text-[11px] px-2.5 py-1"
                  } ${
                    sortedData.length > 0
                      ? "border-gray-200 text-gray-500 bg-white hover:border-gray-300 hover:bg-gray-50 active:scale-95 cursor-pointer"
                      : "border-gray-200 text-gray-300 bg-white cursor-not-allowed opacity-60"
                  }`}
                >
                  <svg
                    className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3v11" />
                    <path d="m7 10 5 5 5-5" />
                    <path d="M4 20h16" />
                  </svg>
                  Download CSV
                </button> */}

                {/* Hint tooltip when no changes made yet */}
                {!hasChanges && !isSmall && (
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 text-gray-400 text-[10px] cursor-default select-none hover:bg-gray-50 hover:border-gray-400 hover:text-gray-500 transition-colors"
                    title="⠿ Drag a column header to reorder · Drag the right edge of a header to resize · Use 'Save layout' to persist your changes"
                  >
                    ?
                  </span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Centre: First / prev / page-numbers / next / last */}
        <div className="flex items-center gap-1 text-xs">
          {/* First */}
          <button
            onClick={() => handlePageSet(1)}
            disabled={effectivePage === 1}
            title="First page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100 text-xs`}
          >
            «
          </button>

          {/* Previous */}
          <button
            onClick={handlePreviousPage}
            disabled={effectivePage === 1}
            title="Previous page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            ‹
          </button>

          {/* Page number buttons — always 7 fixed slots to prevent layout shift */}
          {getPageButtons().map((btn, idx) =>
            btn === null ? (
              // invisible placeholder — holds the same space as a real button
              <span
                key={`empty-${idx}`}
                aria-hidden
                className={`inline-block ${
                  isSmall ? "min-w-5 h-5" : "min-w-7 h-7"
                } invisible`}
              />
            ) : btn === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className={`inline-flex items-center justify-center select-none ${
                  isSmall ? "min-w-5 h-5 text-[10px]" : "min-w-7 h-7 text-xs"
                }`}
              >
                …
              </span>
            ) : (
              <button
                key={btn}
                onClick={() => handlePageSet(btn as number)}
                className={`${
                  isSmall
                    ? "min-w-5 h-5 px-1 text-[10px]"
                    : "min-w-7 h-7 px-1 text-xs"
                } border rounded ${
                  effectivePage === btn
                    ? "bg-[#f1935c] text-white border-[#f1935c] font-semibold"
                    : "hover:bg-gray-100"
                }`}
              >
                {btn}
              </button>
            ),
          )}

          {/* Next */}
          <button
            onClick={handleNextPage}
            disabled={effectivePage === totalPages || totalPages === 0}
            title="Next page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            ›
          </button>

          {/* Last */}
          <button
            onClick={() => handlePageSet(totalPages)}
            disabled={effectivePage === totalPages || totalPages === 0}
            title="Last page"
            className={`${
              isSmall ? "px-1 py-0.5" : "px-2 py-1"
            } border rounded disabled:opacity-40 hover:bg-gray-100`}
          >
            »
          </button>
        </div>

        {/* Right: jump-to-page + rows-per-page */}
        <div
          style={{ paddingRight: "5rem" }}
          className="flex items-center gap-3"
        >
          {/* Jump to page */}
          <div className="flex items-center gap-1">
            <span className="text-gray-500 whitespace-nowrap">Go to</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJump()}
              placeholder="#"
              className={`${
                isSmall ? "w-10 text-[10px] py-0.5" : "w-14 text-xs py-1"
              } border rounded px-1 text-center outline-none focus:border-[#f1935c]`}
            />
            <button
              onClick={handleJump}
              className={`${
                isSmall ? "px-1 py-0.5 text-[10px]" : "px-2 py-1 text-xs"
              } border rounded bg-[#f1935c] text-white hover:bg-[#f1935c]`}
            >
              Go
            </button>
          </div>

          {/* Rows per page */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-500 whitespace-nowrap">Rows</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className={`!h-7 ${
                isSmall ? " py-0.5" : " py-1"
              } border rounded px-1 outline-none focus:border-[#f1935c] cursor-pointer text-xs`}
            >
              {[25, 50, 96, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
